import {
  Box,
  Button,
  TextField,
  Typography,
  FormControl,
  Select,
  MenuItem,
  CircularProgress,
  FormHelperText,
  FormControlLabel,
  Radio,
  RadioGroup,
} from '@mui/material'
import React, { useCallback, useState } from 'react'
import {
  createCampPatient,
  updateCampPatientApi,
} from '../../../services/CampManagementService'
import { toast } from 'react-toastify'
import {
  genderOptions,
  preferredCommunicationOptions,
  salutationOptions,
  errorMessages,
} from '../constants/Constants'
import { Formik, Form, Field, ErrorMessage } from 'formik'
import * as Yup from 'yup'
import styles from '../listOfCamps.module.scss'
import { getSmsFlag, getPreferredCommunication } from '../../../utils/dateUtils'

interface campDetails {
  campId: string
  name: string
  description: string
  campTypeId: string
  campType: string
  location: string
  locationId: string
  date: string
  numberOfRegistrations: number | null
  numberOfVisits: number | null
  compliance: string | null
  followUpActions: string | null
  reportUrl: string
}

interface PatientRegistrationProps {
  camp: campDetails
  patient?: any
  isEditMode?: boolean
  onSuccess?: () => void
  listPatientId?: string
  forceCreate?: boolean
}

// Validation Schema
const validationSchema = Yup?.object()?.shape({
  salutation: Yup?.string(),
  firstName: Yup?.string()?.required(
    errorMessages?.camp?.patient?.firstNameRequired
  ),
  middleName: Yup?.string(),
  lastName: Yup?.string()?.required(
    errorMessages?.camp?.patient?.lastNameRequired
  ),
  gender: Yup?.string()?.required(errorMessages?.camp?.patient?.genderRequired),
  dob: Yup?.string()?.required(errorMessages?.camp?.patient?.dobRequired),
  age: Yup?.string(),
  phoneNumber: Yup?.string()
    ?.matches(/^\d{10}$/, errorMessages?.camp?.patient?.phoneNumber?.invalid)
    ?.required(errorMessages?.camp?.patient?.phoneNumber?.required),
  preferredCommunication: Yup?.string()?.required(
    errorMessages?.camp?.patient?.preferredCommunicationRequired
  ),
  emailId: Yup?.string()?.email(errorMessages?.camp?.patient?.emailInvalid),
  town: Yup?.string(),
  postalCode: Yup?.string()
    ?.matches(/^\d{6}$/, errorMessages?.camp?.patient?.postalCode?.invalid)
    ?.required(errorMessages?.camp?.patient?.postalCode?.required),
  employeeId: Yup?.string(),
  volunteerId: Yup?.string(),
  department: Yup?.string(),
})

const PatientRegistration = ({
  camp,
  patient,
  isEditMode: propIsEditMode = true,
  onSuccess,
  listPatientId,
  forceCreate = false,
}: PatientRegistrationProps) => {
  const [isEdit] = useState(propIsEditMode)
  const [, setIsLoading] = useState(false)

  // Helper functions
  const getDOBFromAge = (years: number) => {
    const today = new Date()
    today?.setFullYear((today?.getFullYear?.() || 0) - years)
    return today?.toISOString?.()?.slice?.(0, 10) || ''
  }

  const getAgeFromDOB = (dobStr: string) => {
    if (!dobStr) return ''
    const today = new Date()
    const dobDate = new Date(dobStr)
    let age = (today?.getFullYear?.() || 0) - (dobDate?.getFullYear?.() || 0)
    const m = (today?.getMonth?.() || 0) - (dobDate?.getMonth?.() || 0)
    if (
      m < 0 ||
      (m === 0 && (today?.getDate?.() || 0) < (dobDate?.getDate?.() || 0))
    ) {
      age--
    }
    return age > 0 ? String(age) : ''
  }

  // Initial form values
  const getInitialValues = useCallback(() => {
    if (patient) {
      const preferredComm = getPreferredCommunication(patient?.sms)

      return {
        salutation: patient?.salutation || '',
        firstName: patient?.firstName || patient?.name?.given?.[0] || '',
        middleName: patient?.middleName || '',
        lastName:
          patient?.lastName || patient?.name?.family || patient?.lastName || '',
        gender: patient?.gender || '',
        dob: patient?.dob || patient?.birthDate || '',
        age: patient?.age ? String(patient?.age) : patient?.ageString || '',
        phoneNumber:
          patient?.mobile || patient?.phoneNumber || patient?.contact || '',
        preferredCommunication: preferredComm,
        emailId: patient?.emailId || patient?.email || '',
        town: patient?.town || patient?.city || '',
        postalCode: patient?.postalCode ? String(patient?.postalCode) : '',
        employeeId: patient?.employeeId || '',
        volunteerId: patient?.volunteerId || '',
        department: patient?.department || '',
      }
    }
    return {
      salutation: '',
      firstName: '',
      middleName: '',
      lastName: '',
      gender: '',
      dob: '',
      age: '',
      phoneNumber: '',
      preferredCommunication: '',
      emailId: '',
      town: '',
      postalCode: '',
      employeeId: '',
      volunteerId: '',
      department: '',
    }
  }, [patient])

  const handleSubmit = async (
    values: any,
    { setSubmitting, resetForm }: any
  ) => {
    if (patient?.patientUhid && !forceCreate) {
      await handleUpdate(values, { setSubmitting, resetForm })
      return
    }

    setIsLoading(true)
    try {
      const payload = {
        registrationType: 'CAMPAIGN',
        service: 'camp',
        selectedLocationId: camp?.locationId
          ? Number(camp?.locationId)
          : undefined,
        selectedLocationName: camp?.location,
        campaignName: camp?.name,
        campId: camp?.campId,
        salutation: values?.salutation,
        firstName: values?.firstName,
        middleName: values?.middleName,
        lastName: values?.lastName,
        gender: values?.gender,
        dob: values?.dob,
        age: values?.age ? Number(values.age) : '',
        phoneNumber: values?.phoneNumber,
        sms: getSmsFlag(values?.preferredCommunication),
        emailId: values?.emailId,
        town: values?.town,
        postalCode: values?.postalCode ? Number(values?.postalCode) : '',
        employeeId: values?.employeeId || '',
        volunteerId: values?.volunteerId || '',
        photo: '',
        department: values?.department || '',
      }

      const resCreateCamp = await createCampPatient(payload)
      if (resCreateCamp?.success) {
        toast.success(
          errorMessages?.camp?.patient?.registration?.success ||
            'Patient registered successfully',
          {
            position: 'top-right',
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          }
        )
        resetForm()
        // Call onSuccess callback if provided
        if (onSuccess) {
          onSuccess()
        }
      } else {
        resetForm()
        toast.error(
          resCreateCamp?.message ||
            errorMessages?.camp?.patient?.registration?.error ||
            'Error registering patient',
          {
            position: 'top-right',
            autoClose: 5000,
          }
        )
      }
    } catch (error) {
      resetForm()
      toast.error(
        errorMessages?.camp?.patient?.registration?.genericError ||
          'An error occurred while registering the patient',
        {
          position: 'top-right',
          autoClose: 5000,
        }
      )
    } finally {
      resetForm()
      setIsLoading(false)
      setSubmitting(false)
    }
  }
  const handleUpdate = async (
    values: any,
    { setSubmitting, resetForm }: any
  ) => {
    setIsLoading(true)
    try {
      const payload = {
        identifier: patient?.patientFhirId || '',
        id: '1',
        registrationType: 'CAMPAIGN',
        service: 'camp',
        selectedLocationId: camp?.locationId
          ? Number(camp?.locationId)
          : undefined,
        selectedLocationName: camp?.location,
        campaignName: camp?.name,
        campId: camp?.campId,
        salutation: values?.salutation,
        firstName: values?.firstName,
        middleName: values?.middleName,
        lastName: values?.lastName,
        gender: values?.gender,
        dob: values?.dob,
        age: values?.age ? Number(values.age) : '',
        phoneNumber: values?.phoneNumber,
        sms: getSmsFlag(values?.preferredCommunication),
        emailId: values?.emailId,
        town: values?.town,
        postalCode: values?.postalCode ? Number(values?.postalCode) : '',
        employeeId: values?.employeeId || '',
        photo: '',
        volunteerId: values?.volunteerId || '',
        department: values?.department || '',
        grade: patient?.grade ?? '',
        category: '',
        checkIn: patient?.checkIn,
        note: 'Patient updated details',
      }

      const resCreateCamp = await updateCampPatientApi(payload)
      if (resCreateCamp?.success) {
        toast.success(
          errorMessages?.camp?.patient?.registration?.update ||
            (resCreateCamp?.message ??
              resCreateCamp?.data?.message ??
              'Patient Updated successfully'),
          {
            position: 'top-right',
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          }
        )
        resetForm()
        // // Call onSuccess callback if provided
        if (onSuccess) {
          onSuccess()
        }
      } else {
        toast.error(
          resCreateCamp?.message ||
            errorMessages?.camp?.patient?.registration?.error ||
            'Error updating patient',
          {
            position: 'top-right',
            autoClose: 5000,
          }
        )
      }
    } catch (error) {
      toast.error(
        errorMessages?.camp?.patient?.registration?.genericError ||
          'An error occurred while updating the patient',
        {
          position: 'top-right',
          autoClose: 5000,
        }
      )
    } finally {
      setIsLoading(false)
      setSubmitting(false)
    }
  }

  const RequiredAsterisk = () => (
    <span className={styles.patientRegAsterisk}>*</span>
  )

  // Custom input component that works with Formik
  const FormikTextField = ({ field, form, ...props }: any) => {
    const { name } = field || {}
    const { touched, errors } = form || {}
    const error = touched?.[name] && errors?.[name]

    return (
      <div style={{ flex: 1, maxWidth: '380px' }}>
        <TextField
          {...field}
          {...props}
          error={!!error}
          helperText={error}
          size='small'
          margin='none'
          variant='standard'
          fullWidth
          FormHelperTextProps={{
            style: {
              marginLeft: 0,
              marginTop: 6,
              marginBottom: error ? 12 : 0,
              fontSize: '0.75rem',
              lineHeight: 1.3,
            },
          }}
          InputProps={{
            readOnly: !isEdit,
            disableUnderline: true,
            className:
              'patient-reg-input patient-reg-control patient-reg-control--narrow',
            ...props.InputProps,
          }}
        />
      </div>
    )
  }

  // Custom select component that works with Formik
  const FormikSelect = ({ field, form, options, ...props }: any) => {
    const { name, value } = field || {}
    const { setFieldValue, touched, errors } = form || {}
    const error = touched?.[name] && errors?.[name]

    return (
      <FormControl
        size='small'
        margin='none'
        className={`${styles.patientRegSelectCtrl} ${styles.patientRegControl} ${styles.patientRegControlNarrow}`}
        error={!!error}
        fullWidth
      >
        <Select
          {...field}
          {...props}
          value={value}
          onChange={(e) => setFieldValue(name, e?.target?.value)}
          displayEmpty
          className={styles.patientRegSelect}
          disabled={!isEdit || props?.disabled}
          renderValue={(selected) => {
            if (!selected)
              return (
                <em>
                  {errorMessages?.camp?.patient?.selectOption ||
                    'Select an option'}
                </em>
              )
            const selectedOption = options?.find(
              (opt: any) => opt?.value === selected
            )
            return selectedOption?.label || selected
          }}
        >
          <MenuItem value=''>
            <em>{errorMessages?.camp?.patient?.selectOption}</em>
          </MenuItem>
          {options?.map((option: any) => (
            <MenuItem key={option?.value} value={option?.value}>
              {option?.label}
            </MenuItem>
          ))}
        </Select>
        {error && <FormHelperText>{error}</FormHelperText>}
      </FormControl>
    )
  }

  // Component to handle DOB and age sync
  const DateOfBirthField = ({ form, field, ...props }: any) => {
    const { setFieldValue } = form || {}

    const handleDobChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newDob = e?.target?.value
      setFieldValue('dob', newDob)
      if (newDob) {
        const age = getAgeFromDOB(newDob)
        setFieldValue('age', age)
      } else {
        setFieldValue('age', '')
      }
    }

    return (
      <>
        <TextField
          type='date'
          value={field?.value || ''}
          onChange={handleDobChange}
          size='small'
          margin='none'
          variant='standard'
          InputLabelProps={{ shrink: true }}
          InputProps={{
            readOnly: !isEdit,
            disableUnderline: true,
            className:
              'patient-reg-input patient-reg-control patient-reg-control--narrow',
          }}
          {...props}
        />
        <ErrorMessage name='dob'>
          {(msg) => <FormHelperText error>{msg}</FormHelperText>}
        </ErrorMessage>
      </>
    )
  }

  return (
    <Formik
      initialValues={getInitialValues()}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      {({ setFieldValue, isSubmitting }) => (
        <Form>
          <Box
            display='flex'
            justifyContent='center'
            alignItems='center'
            bgcolor='background.default'
            className={styles.patientRegRoot}
          >
            <Box className={styles.patientRegForm}>
              <Typography
                fontSize={20}
                fontWeight={'bold'}
                align='center'
                className={styles.patientRegTitle}
                mb={2}
              >
                Registration
              </Typography>

              {/* Camp Name */}
              <div className={styles.patientRegRow}>
                <label className={styles.patientRegLabel}>Camp Name</label>
                <TextField
                  disabled={Boolean(listPatientId)}
                  value={camp?.name}
                  size='small'
                  margin='none'
                  variant='standard'
                  InputProps={{
                    readOnly: true,
                    disableUnderline: true,
                    className:
                      'patient-reg-input patient-reg-control patient-reg-control--narrow',
                  }}
                />
              </div>

              {/* Employee ID */}
              <div className={styles.patientRegRow}>
                <label className={styles.patientRegLabel}>Employee ID</label>
                <Field
                  name='employeeId'
                  component={FormikTextField}
                  placeholder='Enter employee ID'
                />
              </div>

              {/* Volunteer ID */}
              <div className={styles.patientRegRow}>
                <label className={styles.patientRegLabel}>Volunteer ID</label>
                <Field
                  name='volunteerId'
                  component={FormikTextField}
                  placeholder='Enter volunteer ID'
                />
              </div>

              {/* Salutation Dropdown */}
              <div className={styles.patientRegRow}>
                <label className={styles.patientRegLabel}>Salutation</label>
                <Field
                  name='salutation'
                  component={FormikSelect}
                  options={salutationOptions}
                />
              </div>

              {/* First Name */}
              <div className={styles.patientRegRow}>
                <label className={styles.patientRegLabel}>
                  First Name <RequiredAsterisk />
                </label>
                <Field name='firstName' component={FormikTextField} required />
              </div>

              {/* Middle Name */}
              <div className={styles.patientRegRow}>
                <label className={styles.patientRegLabel}>Middle Name</label>
                <Field name='middleName' component={FormikTextField} />
              </div>

              {/* Last Name */}
              <div className={styles.patientRegRow}>
                <label className={styles.patientRegLabel}>
                  Last Name <RequiredAsterisk />
                </label>
                <Field name='lastName' component={FormikTextField} required />
              </div>

              {/* Gender */}
              <div className={styles.patientRegRow}>
                <label className={styles.patientRegLabel}>
                  Gender <RequiredAsterisk />
                </label>
                <div className={styles.genderRadioGroup}>
                  <Field name='gender'>
                    {({ field }: any) => (
                      <RadioGroup
                        {...field}
                        row
                        className={styles.genderRadioOptions}
                      >
                        {genderOptions?.map((option) => (
                          <FormControlLabel
                            key={option?.value}
                            value={option?.value}
                            control={
                              <Radio
                                disabled={patient?.patientUhid ? true : false}
                                className={styles.genderRadio}
                                size='small'
                              />
                            }
                            label={option?.label}
                            className={styles.genderRadioLabel}
                          />
                        ))}
                      </RadioGroup>
                    )}
                  </Field>
                  <ErrorMessage name='gender'>
                    {(msg) => (
                      <FormHelperText error className={styles.errorMessage}>
                        {msg}
                      </FormHelperText>
                    )}
                  </ErrorMessage>
                </div>
              </div>

              <div className={styles.patientRegRow}>
                <label className={styles.patientRegLabel}>
                  Department Name
                </label>
                <Field
                  name='department'
                  component={FormikTextField}
                  type='text'
                  inputProps={{ className: 'no-arrows' }}
                  // required
                />
              </div>

              {/* Location */}
              <div className={styles.patientRegRow}>
                <label className={styles.patientRegLabel}>
                  Location <RequiredAsterisk />
                </label>
                <TextField
                  value={camp?.location}
                  size='small'
                  margin='none'
                  variant='standard'
                  InputProps={{
                    readOnly: true,
                    disableUnderline: true,
                    className:
                      'patient-reg-input patient-reg-control patient-reg-control--narrow',
                  }}
                />
              </div>

              {/* DOB */}
              <div className={styles.patientRegRow}>
                <label className={styles.patientRegLabel}>
                  DOB <RequiredAsterisk />
                </label>
                <Field
                  name='dob'
                  component={DateOfBirthField}
                  // label='Date of Birth'
                />
              </div>

              {/* Age */}
              <div className={styles.patientRegRow}>
                <label className={styles.patientRegLabel}>Age</label>
                <Field
                  name='age'
                  component={FormikTextField}
                  type='number'
                  inputProps={{ className: 'no-arrows' }}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const newAge = e?.target?.value
                    setFieldValue('age', newAge)
                    if (
                      newAge &&
                      !isNaN(Number(newAge)) &&
                      Number(newAge) > 0
                    ) {
                      const dob = getDOBFromAge(Number(newAge))
                      setFieldValue('dob', dob)
                    } else {
                      setFieldValue('dob', '')
                    }
                  }}
                />
              </div>

              {/* Phone Number */}
              <div className={styles.patientRegRow}>
                <label className={styles.patientRegLabel}>
                  Phone Number <RequiredAsterisk />
                </label>
                <Field
                  name='phoneNumber'
                  component={FormikTextField}
                  type='tel'
                  inputProps={{ className: 'no-arrows' }}
                  required
                />
              </div>

              {/* Preferred Communication Dropdown */}
              <div className={styles.patientRegRow}>
                <label className={styles.patientRegLabel}>
                  Preferred Communication <RequiredAsterisk />
                </label>
                <Field
                  name='preferredCommunication'
                  component={FormikSelect}
                  options={preferredCommunicationOptions}
                  required
                />
              </div>

              {/* Email */}
              <div className={styles.patientRegRow}>
                <label className={styles.patientRegLabel}>Email</label>
                <Field
                  name='emailId'
                  component={FormikTextField}
                  type='email'
                />
              </div>

              {/* Town */}
              <div className={styles.patientRegRow}>
                <label className={styles.patientRegLabel}>Town</label>
                <Field name='town' component={FormikTextField} />
              </div>

              {/* Postal Code */}
              <div className={styles.patientRegRow}>
                <label className={styles.patientRegLabel}>
                  Postal Code <RequiredAsterisk />
                </label>
                <Field
                  name='postalCode'
                  component={FormikTextField}
                  type='number'
                  inputProps={{ className: 'no-arrows' }}
                />
              </div>

              {isEdit && (
                <Box
                  display='flex'
                  gap={1}
                  justifyContent='center'
                  mt={2}
                  className={styles.patientRegSaveRow}
                >
                  <Button
                    type='submit'
                    variant='contained'
                    color='primary'
                    size='small'
                    disabled={isSubmitting}
                    startIcon={
                      isSubmitting ? (
                        <CircularProgress size={20} color='inherit' />
                      ) : null
                    }
                    className={styles.patientRegSaveBtn}
                  >
                    {/* {isSubmitting ? 'Saving...' : 'Save'} */}
                    {isSubmitting
                      ? 'Saving...'
                      : patient?.patientUhid
                      ? 'Update'
                      : 'Save'}
                  </Button>
                </Box>
              )}
            </Box>
          </Box>
        </Form>
      )}
    </Formik>
  )
}

export default PatientRegistration
